import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, ShoppingCart, ArrowLeft, Type, Palette, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const FONT_OPTIONS = [
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Manrope', label: 'Manrope' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Impact', label: 'Impact' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS' },
];

export function TemplateEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [customizations, setCustomizations] = useState({
    heading: 'Your Business Name',
    subheading: 'Your Tagline Here',
    name: 'John Doe',
    title: 'Creative Director',
    phone: '+961 1 234 567',
    email: 'info@yourbusiness.com',
    address: 'Beirut, Lebanon',
    primary_color: '#6BCDB0',
    secondary_color: '#2D3E60',
    accent_color: '#E85A72',
    heading_font: 'Outfit',
    body_font: 'Manrope',
  });

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await api.get(`/api/templates/${id}`);
        setTemplate(res.data);
      } catch {
        toast.error('Template not found');
        navigate('/templates');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [id]);

  const handleChange = (key, value) => {
    setCustomizations(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!user) {
      toast.error('Please login to save designs');
      navigate('/login');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/api/templates/${id}/customize`, {
        template_id: id,
        customizations,
      });
      toast.success('Design saved!');
    } catch {
      toast.error('Failed to save design');
    } finally {
      setSaving(false);
    }
  };

  const handleOrder = () => {
    navigate(`/custom-design?template=${id}&customized=true`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/templates')} size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-navy">Customize Template</h1>
              <p className="text-gray-500 text-sm">{template.name} - {template.category}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-design-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Design'}
            </Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleOrder} data-testid="order-design-btn">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Order This Design
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Controls Panel */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden sticky top-24"
            >
              <Tabs defaultValue="text">
                <TabsList className="w-full border-b border-gray-200 rounded-none bg-gray-50 p-1">
                  <TabsTrigger value="text" className="flex-1 rounded-lg">
                    <Type className="h-4 w-4 mr-2" /> Text Fields
                  </TabsTrigger>
                  <TabsTrigger value="colors" className="flex-1 rounded-lg">
                    <Palette className="h-4 w-4 mr-2" /> Colors
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="text" className="p-5 space-y-4">
                  {[
                    { key: 'heading', label: 'Business Name' },
                    { key: 'subheading', label: 'Tagline' },
                    { key: 'name', label: 'Your Name' },
                    { key: 'title', label: 'Job Title' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'email', label: 'Email' },
                    { key: 'address', label: 'Address' },
                  ].map(field => (
                    <div key={field.key}>
                      <Label className="text-navy text-xs font-medium">{field.label}</Label>
                      <Input
                        value={customizations[field.key]}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="mt-1"
                        data-testid={`editor-${field.key}-input`}
                      />
                    </div>
                  ))}

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                    <div>
                      <Label className="text-navy text-xs font-medium">Heading Font</Label>
                      <Select value={customizations.heading_font} onValueChange={(v) => handleChange('heading_font', v)}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-navy text-xs font-medium">Body Font</Label>
                      <Select value={customizations.body_font} onValueChange={(v) => handleChange('body_font', v)}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="colors" className="p-5 space-y-5">
                  {[
                    { key: 'primary_color', label: 'Primary Color' },
                    { key: 'secondary_color', label: 'Secondary Color' },
                    { key: 'accent_color', label: 'Accent Color' },
                  ].map(field => (
                    <div key={field.key}>
                      <Label className="text-navy text-xs font-medium">{field.label}</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <input
                          type="color"
                          value={customizations[field.key]}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                          data-testid={`editor-${field.key}`}
                        />
                        <Input
                          value={customizations[field.key]}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          className="flex-1 font-mono text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>

          {/* Live Preview */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Check className="h-4 w-4 text-mint" />
                <span className="text-sm font-medium text-navy">Live Preview</span>
              </div>

              {/* Preview Card */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
                {/* Template Image */}
                <div className="relative">
                  <img
                    src={template.preview_url}
                    alt={template.name}
                    className="w-full aspect-[16/9] object-cover"
                  />
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${customizations.primary_color}dd, ${customizations.secondary_color}dd)` }}
                  >
                    <h1
                      className="text-4xl sm:text-5xl font-black text-white text-center drop-shadow-lg"
                      style={{ fontFamily: customizations.heading_font }}
                    >
                      {customizations.heading}
                    </h1>
                    <p
                      className="text-lg text-white/90 mt-2 text-center"
                      style={{ fontFamily: customizations.body_font }}
                    >
                      {customizations.subheading}
                    </p>
                  </div>
                </div>

                {/* Business Card Preview */}
                <div className="p-8">
                  <div
                    className="rounded-2xl p-8 relative overflow-hidden"
                    style={{ backgroundColor: customizations.secondary_color }}
                  >
                    <div
                      className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20"
                      style={{ backgroundColor: customizations.primary_color, transform: 'translate(30%, -30%)' }}
                    />
                    <div
                      className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-15"
                      style={{ backgroundColor: customizations.accent_color, transform: 'translate(-30%, 30%)' }}
                    />
                    <div className="relative z-10">
                      <h2
                        className="text-2xl font-bold text-white"
                        style={{ fontFamily: customizations.heading_font }}
                      >
                        {customizations.name}
                      </h2>
                      <p
                        className="text-sm mt-1"
                        style={{ color: customizations.primary_color, fontFamily: customizations.body_font }}
                      >
                        {customizations.title}
                      </p>
                      <div
                        className="w-12 h-0.5 mt-4 mb-4"
                        style={{ backgroundColor: customizations.accent_color }}
                      />
                      <div
                        className="space-y-1 text-sm text-white/80"
                        style={{ fontFamily: customizations.body_font }}
                      >
                        <p>{customizations.phone}</p>
                        <p>{customizations.email}</p>
                        <p>{customizations.address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Color Swatches */}
                  <div className="flex items-center gap-3 mt-6">
                    <span className="text-xs text-gray-500">Colors:</span>
                    {[customizations.primary_color, customizations.secondary_color, customizations.accent_color].map((color) => (
                      <div key={color} className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full border border-gray-200" style={{ backgroundColor: color }} />
                        <span className="text-xs font-mono text-gray-500">{color}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
